import { Box, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { faTrash, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonText, OutlineCard, Table, useToast } from '@morressier/alembic';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import {
  fetchWorkspaceRoles,
  fetchSubmissionContextMemberships
} from 'src/api/queries';
import {
  useRemoveJournalMembership,
  useUpdateJournalMembership
} from 'src/api/mutations';
import { pluralizeString } from 'src/utils/string';
import SelectMember from './SelectMember';
import AssignMembers from './AssignMember/AssignMembers';
import { useUnassignedMember } from 'src/hooks/useUnassignedMember';

interface Props {
  workspaceId: string;
  journalId: string;
  journalName: string;
}

const LIMIT = 10;

export const Membership: React.FC<Props> = ({
  journalId,
  workspaceId,
  journalName
}) => {
  const toast = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const { data: orgRoles } = useQuery({
    queryKey: ['workspace-roles', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceRoles(workspaceId) : null)
  });

  const roles = Object.keys(orgRoles?.roleConfigs ?? {});

  const { isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, data } =
    useInfiniteQuery({
      retry: 0,
      queryKey: ['journal-memberships', { journalId }],
      queryFn: ({ pageParam = 0 }) =>
        fetchSubmissionContextMemberships(journalId, {
          limit: LIMIT,
          offset: pageParam
        }),
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage) return undefined;
        const maxOffset = lastPage.total;
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < maxOffset ? nextOffset : undefined;
      }
    });

  const toggleModal = () => setIsOpen(prev => !prev);

  const AssignMemberButton = ({ disabled }: { disabled?: boolean }) => {
    const { total: unassignedTotal, isLoading: isUnassignedLoading } =
      useUnassignedMember(workspaceId, '', 1);

    const isEmpty = !isUnassignedLoading && unassignedTotal === 0;

    return (
      <Tooltip
        hasArrow
        shouldWrapChildren
        placement="top"
        label="No more members from this workspace can be added to this journal."
        borderRadius={4}
        isDisabled={!isEmpty}
      >
        <Button
          iconLeft={<FontAwesomeIcon icon={faUserPlus} />}
          width={{ base: 'full', md: 200 }}
          onClick={toggleModal}
          isDisabled={disabled || isEmpty}
        >
          Assign Member
        </Button>
      </Tooltip>
    );
  };

  const removeMembershipMutation = useRemoveJournalMembership(
    journalId,
    workspaceId
  );
  const updateMembershipMutation = useUpdateJournalMembership(journalId);

  const onRemoveMembership = async (assignmentId: string) => {
    const res = await removeMembershipMutation.mutateAsync(assignmentId);

    if (res.isSuccess) {
      return toast({
        variant: 'success',
        title: 'Member Assignment Removed',
        isClosable: true
      });
    }

    return toast({
      variant: 'error',
      title: res.message,
      isClosable: true
    });
  };

  const total = data?.pages[0]?.total || 0;
  const isEmpty = total === 0;

  const isDisabled =
    updateMembershipMutation.isLoading || removeMembershipMutation.isLoading;

  return (
    <Flex flexDirection={'column'} gap={4}>
      <OutlineCard borderless paddingX={4} paddingY={6}>
      <Flex alignItems="center" justifyContent="space-between">
        <Text textStyle={'headingSection'}>Member Assignments</Text>
        {!isEmpty && <AssignMemberButton disabled={isDisabled} />}
      </Flex>
      {isLoading ? (
        <Spinner />
      ) : isEmpty ? (
        <Flex flexDirection={'column'} gap={2} alignItems="center">
          <Flex
            width={'100%'}
            padding={8}
            justifyContent="center"
            background="neutralSecondaryFill"
            borderRadius="lg"
            fontSize="48px"
          >
            <FontAwesomeIcon icon={faUserPlus} />
          </Flex>
          <Flex
            flexDirection={'column'}
            gap={2}
            alignItems="center"
            marginY={4}
          >
            <Text textStyle="headingGroup">
              No Members have been assigned yet.
            </Text>
            <Text textStyle="textBody">
              Maecenas faucibus mollis interdum. Cum sociis natoque penatibus et
              magnis dis parturient montes.
            </Text>
          </Flex>
          <AssignMemberButton />
        </Flex>
      ) : (
        <>
          <Flex gap={1}>
            <Text fontWeight="semibold">
              {total} {pluralizeString('member', total)}
            </Text>
            <Text>assigned to {journalName}</Text>
          </Flex>
          <Box overflowX="auto" width="100%">
            <Table>
              <Table.HeaderRow gap={2}>
                <Table.HeaderCell flex="5">Name</Table.HeaderCell>
                <Table.HeaderCell flex="6">Email</Table.HeaderCell>
                <Table.HeaderCell width="200px">Role</Table.HeaderCell>
                <Table.HeaderCell flex="2" />
              </Table.HeaderRow>
              <Table.Body>
                {data?.pages.map(({ collection }, i) => (
                  <Fragment key={i}>
                    {collection.map(member => (
                      <Table.Row
                        key={member.id}
                        gap={2}
                        _last={{ borderBottom: 'none' }}
                      >
                        <Table.RowCell flex="5" isTruncated>
                          <Text isTruncated textStyle="headingLine">
                            {member.user.firstName} {member.user.lastName}
                          </Text>
                        </Table.RowCell>
                        <Table.RowCell flex="6" isTruncated>
                          <Text isTruncated>{member.user.email}</Text>
                        </Table.RowCell>
                        <Table.RowCell width="200px" flexDirection={'column'}>
                          <SelectMember
                            member={member}
                            roles={roles}
                            updateMembershipMutation={updateMembershipMutation}
                          />
                        </Table.RowCell>
                        <Table.RowCell
                          flex="2"
                          textStyle="headingLine"
                          justifyContent={'end'}
                        >
                          <ButtonText
                            variant="caution"
                            onClick={() => onRemoveMembership(member.id)}
                            isDisabled={isDisabled}
                          >
                            <Flex
                              gap={1}
                              alignItems={'center'}
                              textStyle="actionLabel"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                              <Text>Delete</Text>
                            </Flex>
                          </ButtonText>
                        </Table.RowCell>
                      </Table.Row>
                    ))}
                  </Fragment>
                ))}
              </Table.Body>
            </Table>
          </Box>
          <Flex justifyContent={'center'}>
            {hasNextPage && (
              <Button
                variant="secondary"
                onClick={() => fetchNextPage()}
                isLoading={isFetchingNextPage}
              >
                Load More
              </Button>
            )}
          </Flex>
        </>
      )}
      {isOpen && (
        <AssignMembers
          isOpen={isOpen}
          onClose={toggleModal}
          submissionContextId={journalId}
          roles={roles}
          journalName={journalName}
          workspaceId={workspaceId}
        />
      )}
      </OutlineCard>
    </Flex>
  );
};
